import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Typed cross-component application events.
 *
 * Type-safe successor to the stringly-typed half of `MessageService`
 * (`sendMessage`/`getMessage`): events are a discriminated union keyed by
 * `kind`, so emitters and subscribers share a compiler-checked payload — no
 * magic-string event names, no `any` payloads.
 *
 * Scope (SCRUM-750): only the server-driven refresh seam
 * (`ProjectWatchService` → these events) emits on this bus today. The remaining
 * `MessageService` event-bus call sites migrate separately; the two buses
 * coexist until then. `MessageService.showMessage` (toasts) is unaffected.
 *
 * Each variant corresponds 1:1 to a server `DataChangeType`
 * (see `ProjectWatchService.toEvent`). SCRUM-751 (live refresh for content
 * edits) adds further variants.
 */
export type AppEvent =
  | { kind: 'reloadVersions' }
  | { kind: 'reloadWorkflows' }
  | { kind: 'refreshProject' }
  // SCRUM-751 content edits. Project-scoped:
  | { kind: 'reloadFiles' }
  | { kind: 'reloadProjectConstants' }
  // Workflow-scoped: `workflowId` is the server `context_id`; subscribers refetch
  // only when it matches the workflow they are viewing.
  | { kind: 'reloadSteps'; workflowId: number }
  | { kind: 'reloadWorkflowFiles'; workflowId: number }
  | { kind: 'reloadWorkflowVariableSchema'; workflowId: number };

@Injectable({
  providedIn: 'root',
})
export class AppEventBus {
  private readonly subject = new Subject<AppEvent>();

  /** Emit a typed event to all current subscribers. */
  emit(event: AppEvent): void {
    this.subject.next(event);
  }

  /**
   * Observe only events of the given `kind`. The emitted value is narrowed to
   * the matching union member, so the payload is fully typed.
   */
  on<K extends AppEvent['kind']>(kind: K): Observable<Extract<AppEvent, { kind: K }>> {
    return this.subject.pipe(
      filter((event): event is Extract<AppEvent, { kind: K }> => event.kind === kind),
    );
  }
}

/**
 * Exhaustiveness guard for a `switch` over a closed union (e.g. the generated
 * `change_type` union). Reaching it with an unhandled case is a compile error,
 * which forces every new `DataChangeType` variant to be routed (SCRUM-751).
 */
export function assertNever(value: never): never {
  throw new Error(`Unexpected value: ${JSON.stringify(value)}`);
}
